import { AskPiersButton } from "../Assistant/PiersAssistant";
import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { hasUnsavedChanges } from "../../helpers/unsaved-changes";
import { getLoggedInUser } from "../../helpers/backend_helper";
import { getData } from "../../helpers/url-request";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../Common/AppOverlay";
import NotificationDropdown from "../CommonForBoth/TopbarDropdown/NotificationDropdown";
import ProfileMenu from "../CommonForBoth/TopbarDropdown/ProfileMenu";
import piersShieldLogo from "../../assets/images/piers-sidebar-logo.png";
import { COPILOT_ENABLED, useContextualHelp } from "../Help/ContextualHelp";
import { useOnboarding } from "../../hooks/useOnboarding";
import {
  canAccessAdmin,
  canAccessFirmWorkspace,
  isSystemContext,
  defaultContextPath,
  isAdminPath,
  isInspectionPath,
} from "./navigation";

const Header = () => {
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [switchFirmConfirmOpen, setSwitchFirmConfirmOpen] = useState(false);
  const [account, setAccount] = useState(null);
  const [canSwitchFirm, setCanSwitchFirm] = useState(false);
  const { openHelp, openCopilot } = useContextualHelp();
  const { gettingStarted, gettingStartedLoading, onboardingSummary } =
    useOnboarding();

  const history = useNavigate();
  const location = useLocation();
  const isFirmAdministrator = canAccessAdmin(account);
  const isAdministration =
    isFirmAdministrator && isAdminPath(location.pathname, account);
  const showOnboarding =
    gettingStarted?.isApplicable && !onboardingSummary.onboardingComplete;
  const onboardingPage = location.pathname === "/piers/getting-started";

  useEffect(() => {
    getLoggedInUser().then((user) => setAccount(user || null));
    let cancelled = false;
    getData("/FirmMembership/mine").then((response) => {
      if (!cancelled) setCanSwitchFirm(response.status && Array.isArray(response.data) && response.data.length > 1);
    }).catch(() => {
      if (!cancelled) setCanSwitchFirm(false);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <React.Fragment>
      <header id="page-topbar">
        <div className="navbar-header">
          <NavLink
            to={isSystemContext(account) ? "/dashboard" : defaultContextPath(account)}
            className="reference-header-brand"
          >
            <img src={piersShieldLogo} alt="" />
            <span>
              <strong>PIERS</strong>
              <small>Inspection Platform</small>
            </span>
          </NavLink>
          {!onboardingPage && (
            <nav
              className="reference-header-tabs"
              aria-label="Primary navigation"
            >
              {canAccessFirmWorkspace(account) && <>
              <NavLink
                to="/inspections/list"
                className={() =>
                  isInspectionPath(location.pathname)
                    ? "is-active"
                    : ""
                }
                data-tour="inspection-cycle-nav"
              >
                Inspection Workspace
              </NavLink>
              </>}
              {/* Clients, Dashboard, and Reports are temporarily hidden from the primary navigation. */}
              {isFirmAdministrator && (
                <NavLink
                  to={
                    Number(account?.firmId) === 1
                      ? "/dashboard"
                      : "/firm/dashboard"
                  }
                  className={() => (isAdministration ? "is-active" : "")}
                >
                  Administration
                </NavLink>
              )}
            </nav>
          )}

          <div className="d-flex reference-header-actions">
            {canSwitchFirm ? <NavLink to="/switch-firm" onClick={(event) => { event.preventDefault(); setSwitchFirmConfirmOpen(true); }} className="btn header-item header-firm-context" aria-label="Switch firm" title="Switch firm">
              <i className="bx bx-buildings me-2" aria-hidden="true" />
              <span className="header-firm-context__label">
                <span>{account?.firmName || "Select firm"}</span>
                <small>Switch between firms</small>
              </span>
              <i className="bx bx-chevron-down ms-2" aria-hidden="true" />
            </NavLink> : account?.firmName && (
              <span className="header-item header-firm-context">
                <i className="bx bx-buildings me-2" aria-hidden="true" />
                <span>{account.firmName}</span>
              </span>
            )}
            <AskPiersButton />
            {showOnboarding && onboardingSummary.percentComplete < 100 && (
              <button
                type="button"
                className={`btn header-setup-button${
                  onboardingPage ? " is-active" : ""
                }`}
                onClick={() => history("/piers/getting-started")}
                aria-label={
                  showOnboarding
                    ? `Open onboarding, ${onboardingSummary.percentComplete}% complete`
                    : "Open PIERS guide"
                }
              >
                <FeatherIcon
                  icon={showOnboarding ? "check-circle" : "compass"}
                />
                <span>
                  {showOnboarding
                    ? `Finish Setup - ${onboardingSummary.percentComplete}%`
                    : "PIERS Guide"}
                </span>
                {gettingStartedLoading && (
                  <i className="bx bx-loader-alt bx-spin" aria-hidden="true" />
                )}
              </button>
            )}
            {COPILOT_ENABLED && (
              <button
                type="button"
                className="btn header-copilot-button"
                title="PIERS Copilot"
                aria-label="Open PIERS Copilot"
                data-help-target="piers-copilot"
                onClick={openCopilot}
              >
                <img src="/images/piers-copilot.png" alt="" />
                <span>PIERS Copilot</span>
              </button>
            )}
            <button
              type="button"
              className="btn header-item header-help-button"
              title="Help"
              aria-label="Open help"
              data-help-target="global-help"
              data-tour="help"
              onClick={openHelp}
            >
              <FeatherIcon icon="help-circle" />
              <span>Help</span>
            </button>
            <NotificationDropdown />
            <ProfileMenu canSwitchFirm={canSwitchFirm} onSwitchFirm={() => setSwitchFirmConfirmOpen(true)} />
            <button
              type="button"
              className="btn header-item noti-icon header-logout-button"
              title="Logout"
              aria-label="Logout"
              onClick={() => setLogoutConfirmOpen(true)}
            >
              <FeatherIcon icon="log-out" className="icon-lg" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <Modal
        centered
        isOpen={switchFirmConfirmOpen}
        toggle={() => setSwitchFirmConfirmOpen(false)}
      >
        <ModalHeader toggle={() => setSwitchFirmConfirmOpen(false)}>
          Switch firm?
        </ModalHeader>
        <ModalBody>
          {hasUnsavedChanges()
            ? "You have unsaved changes. Leave this page to choose another firm?"
            : "Do you want to leave this page to choose another firm?"}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setSwitchFirmConfirmOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={() => { setSwitchFirmConfirmOpen(false); history("/switch-firm"); }}>
            Continue
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        centered
        isOpen={logoutConfirmOpen}
        toggle={() => setLogoutConfirmOpen(false)}
      >
        <ModalHeader toggle={() => setLogoutConfirmOpen(false)}>
          Confirm logout
        </ModalHeader>
        <ModalBody>
          {hasUnsavedChanges()
            ? "You have unsaved changes. Discard them and log out?"
            : "Are you sure you want to log out?"}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setLogoutConfirmOpen(false)}>
            Stay
          </Button>
          <Button color="danger" onClick={() => history("/logout")}>
            {hasUnsavedChanges() ? "Discard changes and log out" : "Logout"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Header;
